import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ContactForm from '../components/sections/ContactForm';
import { Phone, Mail, MapPin, Clock, MessageSquare } from 'lucide-react';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#001582] to-[#001ea7] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFC107] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#FFC107] rounded-full" />
              <span className="text-white text-sm font-semibold uppercase tracking-wider">Kontak</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Hubungi <span className="text-[#FFC107]">Kami</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              Tim kami siap membantu kebutuhan engineering dan manufacturing industri Anda. 
              Jangan ragu untuk menghubungi kami.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-8">
                Informasi Kontak
              </h2>
              
              <div className="space-y-6 mb-10">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FFC107]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1a1a1a] mb-1">Alamat</h4>
                    <address className="text-slate-600 not-italic leading-relaxed">
                      Jl. Mawar No. 120A, RT.003/RW.006<br />
                      Mustikasari, Kec. Mustikajaya<br />
                      Kota Bekasi, Jawa Barat 17157
                    </address>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FFC107]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1a1a1a] mb-1">Telepon</h4>
                    <div className="space-y-1">
                      <a href="tel:02182609241" className="block text-slate-600 hover:text-[#001582] transition-colors">
                        021-82609241
                      </a>
                      <a href="tel:02138716184" className="block text-slate-600 hover:text-[#001582] transition-colors">
                        021-38716184
                      </a>
                      <a href="tel:02182595178" className="block text-slate-600 hover:text-[#001582] transition-colors">
                        021-82595178
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#FFC107]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1a1a1a] mb-1">Email</h4>
                    <a href="mailto:admin@imamontaz.id" className="text-slate-600 hover:text-[#001582] transition-colors">
                      admin@imamontaz.id
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#001582] to-[#001ea7] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-[#FFC107]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1a1a1a] mb-1">Jam Operasional</h4>
                    <p className="text-slate-600">Senin - Jumat: 08:00 - 17:00 WIB</p>
                    <p className="text-slate-600">Sabtu: 08:00 - 12:00 WIB</p>
                  </div>
                </div>
              </div>

              {/* Quick Contact Buttons */}
              <div className="flex flex-wrap gap-3 mb-10">
                <a 
                  href="https://wa.me/6285210005588"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#128C7E] transition-colors"
                  data-testid="whatsapp-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                  WhatsApp
                </a>
                <a 
                  href="tel:02182609241"
                  className="inline-flex items-center gap-2 bg-[#FFC107] text-[#001582] px-6 py-3 rounded-xl font-semibold hover:bg-[#FFD54F] transition-colors"
                  data-testid="quick-call-btn"
                >
                  <Phone className="w-4 h-4" />
                  Telepon Sekarang
                </a>
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d988.5!2d107.0041309!3d-6.3065832!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e699300502bc0d1%3A0x801ebd800f41580!2sPT.Ima%20Montaz%20Teknindo!5e0!3m2!1sen!2sid!4v1700000000000!5m2!1sen!2sid"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="PT IMA MONTAZ TEKNINDO Location"
                  className="rounded-2xl"
                  data-testid="google-map"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
